<template>
  <div class="module-plan-meta-data">
    <div class="wrapper">
      <div class="module-plan-meta-data-header">
        <div v-if="sequentialSectionCompletionReq" class="component-content">
          <cv-form class="meta-data-header">
            <dl v-if="sequentialSectionCompletionReq" class="field-set section-prerequisite">
              <dd class="field-value">
                <span class="icon">
                  <svg-icon name="Lock24" kind="indicative" />
                </span>
                <span class="text">{{ t("module_plan_sequential_section_access_label") }}</span>
                <cv-tooltip
                  direction="bottom"
                  :alignment="tooltipAlignment"
                  :tip="t('module_plan_sequential_section_access_tooltip_text')"
                >
                  <svg-icon name="Info32" kind="indicative" />
                </cv-tooltip>
              </dd>
            </dl>
          </cv-form>
        </div>
        <div v-if="isTeacher" class="action-buttons">
          <cv-button
            v-if="isAssignmentLesson"
            class="icon-button monitor"
            kind="ghost"
            @click="handleMonitor"
          >
            <cv-tooltip
              direction="bottom"
              alignment="center"
              :tip="t('module_plan_monitoring_button_tooltip_text')"
            >
              <svg-icon name="Monitor24" kind="action" />
            </cv-tooltip>
          </cv-button>

          <cv-button class="icon-button printlite" kind="ghost" @click="handlePrintView">
            <cv-tooltip
              direction="bottom"
              alignment="end"
              :tip="t('module_plan_print_module_plan_button_tooltip_text')"
            >
              <svg-icon name="PrintLite24" kind="action" />
            </cv-tooltip>
          </cv-button>
        </div>
      </div>

      <!--      TODO: [PLANNER] add back when populating module progress-->
      <div v-if="hasSchedule && hasAttemptStatusInfo" class="module-plan-meta-data-content">
        <cv-form v-if="hasSchedule" class="schedule">
          <dl class="field-set type-date">
            <dd class="field-value">
              <div class="start">
                <div class="date">{{ startDate }}</div>
                <div class="time">{{ startTime }}</div>
              </div>
              <svg-icon name="CircleArrowRight32" kind="indicative" />
              <div class="end">
                <div class="date">{{ endDate }}</div>
                <div class="time">{{ endTime }}</div>
              </div>
            </dd>
          </dl>
        </cv-form>
        <cv-form v-if="hasAttemptStatusInfo" class="status">
          <dl class="field-set attempt-status">
            <dt class="field-label">
              {{ t("module_plan_module_status_label") }}
            </dt>
            <dd class="field-value" :class="{ 'has-status': moduleStatus }">
              <label-component
                v-if="moduleStatus"
                class="attempt-status"
                :kind="attemptStatusInfo.kind"
                :text="attemptStatusInfo.label"
              />
              <stacked-column-bar
                v-if="completionInfo"
                :categories="completionInfoValues"
                :max-value="totalStudent"
                :show-header="false"
                :show-value-count="false"
              />
            </dd>
          </dl>
        </cv-form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { LocationQueryRaw, RouteParamsRaw, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import {
  COMPLETION_STATUS,
  NAMESPACE,
  RESOURCE_DOMAIN,
} from "@modules/search/service/search.constant.ts";
import { ROUTES_VIEW } from "@modules/attempt/service/attempt.constant.ts";
import { RESOURCE_DOMAIN_PATH } from "@plugins/common/constant/resource.constant.ts";
import DateTimeFormatter from "@utils/datetime.formatter.ts";
import DateTimeUtils from "@utils/datetime.utils.ts";
import { isEmpty } from "@utils/var.check.ts";
import DeviceUtils from "@utils/device.utils";
import { useStore } from "vuex";
import StackedColumnBar from "@plugins/common/components/StackedColumnBar.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import LabelComponent from "@plugins/common/components/LabelComponent.vue";
import { StackedColumnBarCategory } from "@plugins/common/types.ts"; // Assuming a type definition exists

interface Props {
  module: {
    uuid: string;
    resourceDomain: string;
    lessonAssignmentUuid?: string | null;
    eAssessment: boolean;
    metaData: {
      uuid: string;
      startDateTime?: string;
      endDateTime?: string;
    };
    moduleAttemptStatus?: string | null;
    overallProgress?: {
      numStudentTotal: number;
      numStudentCompleted: number;
    } | null;
    sequentialSectionCompletionReq?: boolean;
  };
}

const props = defineProps<Props>();

const { t } = useI18n();
const router = useRouter();

const vuexStore = useStore();
const isTeacher = computed(() => vuexStore.getters[`${NAMESPACE.AUTH}/isTeacher`]);

const metaData = computed(() => props.module.metaData);
const moduleStatus = computed(() => props.module.moduleAttemptStatus || null);
const completionInfo = computed(() => props.module.overallProgress || null);
const sequentialSectionCompletionReq = computed(() => props.module.sequentialSectionCompletionReq);

const hasAttemptStatusInfo = computed(() => !!moduleStatus.value || !isEmpty(completionInfo.value));
const hasSchedule = computed(() => !!props.module?.metaData?.startDateTime);
const totalStudent = computed(() => completionInfo.value?.numStudentTotal ?? 0);

const isOverdue = computed(() => {
  const endDateTime = metaData.value?.endDateTime;
  if (!endDateTime) {
    return false;
  }
  const dateTime = DateTimeFormatter.formatLocalDateTimeToDateTime(endDateTime);
  return DateTimeUtils.isNowAfterDate(dateTime);
});

const attemptStatusInfo = computed(() => {
  if (moduleStatus.value === COMPLETION_STATUS.COMPLETED) {
    return { kind: "complete", label: t("complete_label_uppercase") };
  }
  if (isOverdue.value) {
    return { kind: "overdue", label: t("overdue_label_uppercase") };
  }
  return { kind: "incomplete", label: t("incomplete_label_uppercase") };
});

const completionInfoValues = computed((): StackedColumnBarCategory[] => {
  const totalComplete = completionInfo.value?.numStudentCompleted || 0;
  const totalIncomplete = totalStudent.value - totalComplete;

  const categories = [] as StackedColumnBarCategory[];

  if (totalComplete > 0) {
    categories.push({
      name: t("completed_label"),
      cssClass: "completed",
      quantity: totalComplete,
    });
  }

  if (totalIncomplete > 0) {
    categories.push({
      name: isOverdue.value ? t("overdue_label") : t("in_progress_label"),
      cssClass: isOverdue.value ? "overdue" : "in-progress",
      quantity: totalIncomplete,
    });
  }

  return categories;
});

const dateFormatter = (date: string) => {
  return DateTimeFormatter.formatLocalDateTimeToDateOnly(date);
};

const timeFormatter = (time: string) => {
  return DateTimeFormatter.formatLocalDateTimeToTimeAmPm(time);
};

const startDate = computed(() => {
  return hasSchedule.value ? dateFormatter(metaData.value.startDateTime ?? "") : "";
});
const startTime = computed(() => {
  return hasSchedule.value ? timeFormatter(metaData.value.startDateTime ?? "") : "";
});
const endDate = computed(() => {
  return hasSchedule.value ? dateFormatter(metaData.value.endDateTime ?? "") : "";
});
const endTime = computed(() => {
  return hasSchedule.value ? timeFormatter(metaData.value.endDateTime ?? "") : "";
});

const isAssignmentLesson = computed(
  () =>
    props.module.resourceDomain === RESOURCE_DOMAIN.ASSIGNMENT &&
    !!props.module.lessonAssignmentUuid,
);

const tooltipAlignment = computed(() => (DeviceUtils.isMobile() ? "end" : "center"));

const handleMonitor = () => {
  const assignmentUuid = metaData.value?.uuid;
  router.push({
    name: props.module.eAssessment
      ? ROUTES_VIEW.ASSESSMENT.MONITORING
      : ROUTES_VIEW.ASSIGNMENT.MONITORING,
    params: { assignmentUuid },
  });
};

const handlePrintView = () => {
  const routeConfig: { name: string; params: RouteParamsRaw; query: LocationQueryRaw } = {
    name: "LessonPlanPrint",
    params: {
      resourceDomain: RESOURCE_DOMAIN_PATH[props.module.resourceDomain],
      lessonUuid: props.module.uuid,
    },
    query: {},
  };

  if (isAssignmentLesson.value) {
    // For resource, pass the assignment uuid as a query for use in printlite
    routeConfig.query.assignmentUuid =
      props.module?.lessonAssignmentUuid || props.module?.metaData?.uuid || null;
  }

  const routeData = router.resolve(routeConfig);
  window.open(routeData.href, "_blank");
};
</script>

<style lang="scss">
@import "@styles/abstracts/mixins";
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.module-plan-meta-data {
  .module-plan-meta-data-header {
    display: flex;
    align-items: center;
  }

  .section-prerequisite {
    .field-value {
      display: inline-flex;
      align-items: center;
      gap: $spacing-03;
      margin-top: 0;
    }

    .icon {
      display: inline-flex;
    }

    .text {
      @include carbon--type-style("body-short-01", true);
    }
  }

  .action-buttons {
    display: flex;
    align-items: center;
    align-self: flex-end;
    flex: 0 0 auto;
    margin-left: auto;

    .fetch-data-button {
      margin-right: $spacing-03;
    }

    .bx--overflow-menu {
      margin-left: $spacing-03;

      &:hover,
      &:active {
        background-color: $ui-01;
      }
    }

    .icon-button {
      width: 40px;
      height: 40px;
      padding: 0;
      margin-left: $spacing-03;
    }

    .bx--tooltip__trigger {
      svg {
        fill: none;
      }
    }

    &.monitor-button {
      align-self: flex-start;
    }
  }

  .module-plan-meta-data-content {
    display: flex;
    align-items: center;
    gap: $spacing-03 $spacing-05;
    padding: $spacing-05;
    margin-top: $spacing-03;
    background-color: $ui-background;
    border-radius: 10px;

    @include media("mobile") {
      flex-flow: wrap;
    }

    > .bx--form {
      width: 100%;
      padding-bottom: 0;
      margin-top: 0;
      margin-bottom: 0;

      .field-set {
        height: 60px;
        background-color: $ui-01;
        border-radius: 10px;
      }

      &.schedule {
        .field-value {
          display: flex;
          justify-content: space-around;
          align-items: center;
          margin-top: 0;
          padding: $spacing-03;

          > div {
            text-align: center;
          }

          .date {
            @include carbon--type-style("productive-heading-02");
          }

          .time {
            @include carbon--type-style("productive-heading-01");
            color: $text-03;
          }
        }
      }

      &.status {
        margin-top: 0;
        padding-top: 0;
        border-top: none;

        .attempt-status {
          display: flex;
          align-items: center;
          padding: 0 $spacing-06;

          .field-label {
            @include carbon--type-style("card-title-01");
            margin-right: $spacing-05;
            text-transform: uppercase;
          }

          .field-value {
            margin-top: 0;

            &:not(.has-status) {
              flex: 1;
            }

            &:has(.label-component) {
              display: flex;
              justify-content: flex-end;
            }

            .label-component {
              margin: 0;
            }
          }
        }
      }
    }
  }
}
</style>
